import React, { useEffect, useRef } from "react";
import { captureOrder } from "./PayPalServices";

// Simple singleton loader for PayPal SDK script
const paypalScriptCache = {};
function loadPayPalSdk({ clientId, currency }) {
  const key = `${clientId}_${currency}`;
  if (paypalScriptCache[key]) {
    return paypalScriptCache[key];
  }

  paypalScriptCache[key] = new Promise((resolve, reject) => {
    if (window.paypal) {
      resolve(window.paypal);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=${currency}`;
    script.async = true;
    script.onload = () => {
      if (window.paypal) resolve(window.paypal);
      else reject(new Error("PayPal SDK not available after script load"));
    };
    script.onerror = (err) => reject(err);
    document.body.appendChild(script);
  });

  return paypalScriptCache[key];
}

const PayPalButton = ({
  amount,
  eventData,
  registrationID,
  payerEmail,
  onSuccess,
  onError,
  isValidData,
}) => {
  const paypalRef = useRef(null);
  const buttonsInstanceRef = useRef(null);

  useEffect(() => {
    const clientId = eventData?.paypal_clientID || "YOUR_CLIENT_ID";
    if (!clientId) {
      console.warn("PayPal client ID is not provided");
      return;
    }

    if (!amount || !eventData?.paypal_clientID || !paypalRef.current) {
      console.warn("Missing PayPal params or container not ready");
      return;
    }

    paypalRef.current.innerHTML = "";
    let isMounted = true;

    loadPayPalSdk({ clientId, currency: "USD" })
      .then((paypal) => {
        if (!isMounted || !paypalRef.current) return;

        const buttons = paypal.Buttons({
          onClick: function (data, actions) {
            console.log("PayPal Button clicked");
            if (process.env.NODE_ENV !== "production") {
              console.log("PayPal Environment:", clientId);
            }
            // ✅ Perform your validation
            if (!isValidData()) {
              alert("Please complete all required fields before continuing.");
              return actions.reject(); // Stops PayPal flow
            }
            return actions.resolve(); // Continue if valid
          },
          createOrder: (data, actions) => {
            const order = actions.order.create({
              intent: "CAPTURE",
              application_context: {
                shipping_preference: "NO_SHIPPING",
              },
              payer: { email_address: payerEmail },

              purchase_units: [
                {
                  reference_id: String(
                    eventData?.name || " Reg. ID" || registrationID
                  ).slice(0, 127),
                  description: eventData?.name || "Payment",
                  soft_descriptor: "Retreat",
                  amount: {
                    currency_code: "USD",
                    value: parseFloat(amount).toFixed(2),
                  },
                },
              ],
            });
            console.log("Created PayPal order:", order);
            return order;
          },
          onApprove: async (data, actions) => {
            try {
              const details = await actions.order.capture();
              const orderID = details.id;
              console.log("Captured PayPal order:", orderID);
              console.log(
                "Payee email:",
                details.purchase_units[0].payee.email_address
              );
              console.log(
                "Payee merchant ID:",
                details.purchase_units[0].payee.merchant_id
              );
              //console.log("Payment details:", details);
              //captureOrder(orderID, eventData?.id, registrationID);
              onSuccess?.(details);
            } catch (err) {
              console.error("Capture error:", err);
              onError?.(err?.message || JSON.stringify(err));
            }
          },
          onError: (err) => {
            console.error("PayPal Button error:", err);
            onError?.(err?.message || JSON.stringify(err));
          },
        });

        buttonsInstanceRef.current = buttons;
        return buttons.render(paypalRef.current);
      })
      .catch((err) => {
        if (isMounted) {
          console.error("PayPal SDK load error:", err);
          onError?.(err?.message || JSON.stringify(err));
        }
      });

    return () => {
      isMounted = false;
      if (buttonsInstanceRef.current?.close) {
        buttonsInstanceRef.current.close();
      }
    };
  }, [
    amount,
    eventData,
    registrationID,
    payerEmail,
    onSuccess,
    onError,
    isValidData,
  ]);

  return <div ref={paypalRef} />;
};

export default PayPalButton;
